import { timeStringToMs } from './DateFormatter';
import { AssetType, SortDir, StreamType } from '../../api/models/Shared.types';
import { BlackoutUtil } from './BlackoutUtil';
import { DetailTranslationKeys, SettingsKeys } from '../../api/models/TranslationKeys';
import { DateUtil } from './DateUtil';
import { RecordingIcons, Recording } from '../../api/user/RecordingApi.types';
import { Utils } from '@lightningjs/sdk';
import { Backend } from '../../api/Backend';
import {
  IChannelV5,
  IProgramV5,
  IRibbon2Channel,
  IRibbon2Detail,
  IRibbon2Schedule,
  IScheduleTime,
  IScheduleV5,
  ISeasonV5,
  ISeriesV5,
} from '../../api/models/AndromeTV.types';
import { ChannelModel } from '../../api/EpgApi.types';

export interface ISortedProgram {
  program: IProgramV5;
  channel: IChannelV5;
  schedule: IScheduleV5;
  season: ISeasonV5;
  allSchedules: IScheduleV5[];
  series?: ISeriesV5;
}

export interface ISortedSeasons {
  title: string;
  seasonId: string;
  sortedPrograms: ISortedProgram[];
}

export class EpgUtil {
  static epgFromUntil(backend: Backend) {
    const pastDays = backend.globalSettings.numberValue(SettingsKeys.epgPastDays);
    const from = DateUtil.addUTCDays(new Date(), -(pastDays ? pastDays : 7));
    from.setMinutes(0, 0, 0); // truncate to hour, similar to android
    const futureDays = backend.globalSettings.numberValue(SettingsKeys.epgFutureDays);
    const until = DateUtil.addUTCDays(new Date(), futureDays ? futureDays : 7);
    until.setMinutes(0, 0, 0); // truncate to hour, similar to android, else epochSecond requests cause error
    return { from, until };
  }

  /**
   * @returns {ScheduleTimes}
   */
  static getTodayFromUntil() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    return {
      from: today,
      until: tomorrow,
    };
  }

  /**
   * @param {Date} time
   * @param {boolean} replay
   * @returns {{from, until}}
   */
  static fromUntilFromTime(time: Date, replay = false) {
    let from;
    let until;

    if (replay) {
      from = new Date(
        time.getFullYear(),
        time.getMonth(),
        time.getDate(),
        time.getHours() - 24,
        time.getMinutes(),
        0,
        0
      );
      until = new Date(time.getFullYear(), time.getMonth(), time.getDate(), time.getHours(), time.getMinutes(), 0, 0);
    } else {
      from = new Date(time);
      from = DateUtil.toStartOfMinutes(from);
      until = new Date(time);
      until = DateUtil.toStartOfMinutes(DateUtil.addUTCDays(until, 1));
    }

    return { from, until };
  }

  /**
   * @param {Date} time
   * @returns {{from: Date, until: Date}}
   */
  static fromUntilFullDay(time: Date) {
    const from = DateUtil.toStartOfDay(new Date(time));
    const until = DateUtil.addUTCDays(DateUtil.toStartOfDay(new Date(time)), 1);
    return { from, until };
  }

  static scheduleIsReplay(schedule: IScheduleV5, channel: ChannelModel) {
    if (!channel.replayHours || !schedule.published) {
      return false;
    }
    const replayStart = new Date();
    replayStart.setHours(replayStart.getHours() - channel.replayHours);
    return (
      timeStringToMs(schedule.published.start) >= replayStart.getTime() &&
      timeStringToMs(schedule.published.start) < new Date().getTime()
    );
  }

  static findClosestSchedule(schedules: IScheduleV5[] | undefined, replay: boolean, channels?: ChannelModel[]) {
    if (!schedules) {
      return undefined;
    }
    if (replay) {
      this.sortSchedulesByStartDate(schedules, SortDir.Desc);
    } else {
      this.sortSchedulesByStartDate(schedules, SortDir.Asc);
    }

    let filteredSchedules = this.findFirstScheduledItem(schedules, replay, channels);

    // try other way around NEBINT-2316 when nothing is found
    if (!filteredSchedules) {
      filteredSchedules = this.findFirstScheduledItem(schedules, !replay, channels);
    }

    // if no schedule is found take the last one
    if (!filteredSchedules) {
      filteredSchedules = this.findLastAvailableScheduleItems(schedules);
    }
    return filteredSchedules;
  }

  static sortSchedulesByStartDate(schedules: IScheduleV5[] | undefined, dir: SortDir) {
    if (!schedules) {
      return;
    }

    // create map for stable sort. Stable sort is not guaranteed for older Chrome versions (e.g. Tizen)
    const map = new Map();
    schedules.forEach((v, i) => map.set(v, i));

    schedules.sort((obj1, obj2) => {
      let time1;
      let time2;
      if (Number.isNaN(obj1.published.end)) {
        time1 = new Date(obj1.published.end).getTime();
      } else {
        time1 = parseInt(obj1.published.end, 10);
      }

      if (Number.isNaN(obj2.published.end)) {
        time2 = new Date(obj2.published.end).getTime();
      } else {
        time2 = parseInt(obj2.published.end, 10);
      }

      if (time1 < time2) {
        return dir === SortDir.Asc ? -1 : 1;
      } else if (time1 > time2) {
        return dir === SortDir.Asc ? 1 : -1;
      } else {
        if (dir === SortDir.Asc) return (map.get(obj1) ? map.get(obj1) : 0) - (map.get(obj2) ? map.get(obj2) : 0);
        else return 0;
      }
    });
  }

  static findFirstScheduledItem(
    schedules: IScheduleV5[],
    replay: boolean,
    channels: ChannelModel[] = []
  ): IScheduleV5 | undefined {
    if (schedules === undefined || schedules.length === 0) {
      return undefined;
    }

    let fallbackSchedule: IScheduleV5 | undefined = undefined;
    const foundSchedule = schedules.find(schedule => {
      const scheduleStart = timeStringToMs(schedule.published.start);
      const scheduleEnd = timeStringToMs(schedule.published.end);
      const now = new Date().getTime();
      const channel = channels.find(ch => ch.channelId === schedule.channelId);

      if (replay) {
        if (now > scheduleStart) {
          if (channel?.customerHasRight(StreamType.REPLAY)) {
            return true;
          } else if (fallbackSchedule === undefined) {
            fallbackSchedule = schedule;
          }
        } else if (channel?.customerHasRight(StreamType.REPLAY)) {
          fallbackSchedule = schedule;
        }
      } else {
        if ((now >= scheduleStart && now <= scheduleEnd) || now <= scheduleStart) {
          if (channel?.customerHasRight(StreamType.LINEAR)) {
            return true;
          } else if (fallbackSchedule === undefined) {
            fallbackSchedule = schedule;
          }
        } else if (channel?.customerHasRight(StreamType.LINEAR)) {
          fallbackSchedule = schedule;
        }
      }

      return false;
    });

    return foundSchedule || fallbackSchedule;
  }

  static findLastAvailableScheduleItems(schedules: IScheduleV5[]) {
    if (schedules === undefined) {
      return undefined;
    }

    return schedules[schedules.length - 1];
  }

  static scheduleIsLive(schedule?: IScheduleV5) {
    if (!schedule) {
      return false;
    }
    const now = new Date().getTime();
    return timeStringToMs(schedule.published.start) <= now && timeStringToMs(schedule.published.end) >= now;
  }

  static calculateProgressPercentage(scheduleTime: IScheduleTime) {
    if (!scheduleTime) {
      return 0;
    }

    const now = new Date().getTime();
    const start = timeStringToMs(scheduleTime.start);

    if (now < start) {
      return 0;
    }

    const end = timeStringToMs(scheduleTime.end);

    if (now > end) {
      return 0;
    }

    const duration = end - start;
    const elapsed = now - start;
    return elapsed / duration;
  }

  static getPlayIcon(
    backend: Backend,
    schedule?: IRibbon2Schedule | IScheduleV5,
    recording?: Recording,
    channel?: ChannelModel,
    replayStart = undefined,
    onlyRecording = false
  ) {
    // if (anonymousProfileIsActive()) {
    //   return this.playIconAnonymousLogic(schedule, channel, recording, replayStart, onlyRecording);
    // } else {
    return this._playIconLogic(backend, recording, schedule, channel, replayStart, onlyRecording);
    // }
  }

  static _playIconLogic(
    backend: Backend,
    recording?: Recording,
    schedule?: IRibbon2Schedule | IScheduleV5,
    channel?: ChannelModel,
    replayStart?: Date,
    onlyRecording = false
  ): string | undefined {
    if (channel === undefined || schedule === undefined) {
      return undefined;
    }

    if (replayStart === undefined) {
      replayStart = new Date();
      replayStart.setHours(replayStart.getHours() - channel.replayHours);
    }

    const now = new Date().getTime();
    const schedStart = timeStringToMs(schedule.published.start);
    const schedEnd = timeStringToMs(schedule.published.end);

    if (
      !onlyRecording &&
      schedule &&
      schedStart <= now &&
      schedStart > replayStart.getTime() &&
      channel.customerHasRight(StreamType.REPLAY) &&
      !this.scheduleHasBlackout(schedule.blackouts, BlackoutUtil.create(StreamType.REPLAY))
    ) {
      return 'images/replay/replay-ott.png';
    }

    if (
      !onlyRecording &&
      schedule &&
      schedStart <= now &&
      schedEnd >= now &&
      channel.customerHasRight(StreamType.LINEAR) &&
      !this.scheduleHasBlackout(schedule.blackouts, BlackoutUtil.create(StreamType.LINEAR))
    ) {
      return 'images/replay/watch-ott.png';
    }

    if (recording && this.canPlayRecording(backend, recording, schedule, channel)) {
      return 'images/replay/watch-ott.png';
    }

    return undefined;
  }

  static scheduleHasBlackout(scheduleBlackouts: string[] | undefined, blackout: string | undefined) {
    if (!blackout) return undefined;
    return scheduleBlackouts?.some(b => b === blackout);
  }

  static programToRibbon2Detail(program: IProgramV5, seasons: ISeasonV5[] = []): IRibbon2Detail {
    const _season = seasons.find(s => s?.seasonId === program.seasonId);
    return {
      type: AssetType.PROGRAM,
      id: program.programId,
      season: _season?.seasonNumber,
      episode: program.episodeNumber,
      image: program.image?.poster ?? '',
      title: program.text?.title ?? '',
      adult: program.adult,
      synopsis: program.text?.shortDescription ?? '',
      genres: program.genres,
      episodeTitle: program.text?.episodeTitle ?? '',
    };
  }

  static schedulesToRibbon2Channels(schedules: IScheduleV5[]) {
    const channels = new Set(schedules.map(s => s.channelId));
    const result: IRibbon2Channel[] = [];

    channels.forEach(channel => {
      const s = schedules
        .filter(schedule => schedule.channelId === channel)
        .map(schedule => ({
          ...schedule,
          id: schedule.scheduleId,
          programId: schedule.programId,
        }));
      result.push({ id: channel, schedules: s });
    });
    return result;
  }

  static findCurrentSchedule(schedules: IScheduleV5[] | undefined): IScheduleV5 | undefined {
    if (!schedules || schedules.length === 0) {
      return undefined;
    }
    const now = new Date().getTime();

    return schedules.find(sched => {
      const start = timeStringToMs(sched.published.start);
      const end = timeStringToMs(sched.published.end);
      return now >= start && now <= end;
    });
  }

  static sortProgramsPerSeason(
    backend: Backend,
    seasons: ISeasonV5[] | undefined,
    _programs: IProgramV5[] | undefined,
    _schedules: IScheduleV5[] | undefined,
    channels: IChannelV5[] | undefined,
    series: ISeriesV5 | undefined
  ): ISortedSeasons[] {
    const sortedProgramsPerSeason: ISortedSeasons[] = [];
    if (seasons && _programs && _schedules) {
      const now = new Date().getTime();

      seasons.sort((a, b) => {
        if (!a.seasonNumber || !b.seasonNumber) {
          return 1;
        }
        if (a.seasonNumber < b.seasonNumber || !a.seasonNumber) {
          return -1;
        } else if (a.seasonNumber > b.seasonNumber) {
          return 1;
        } else {
          return 0;
        }
      });
      seasons.forEach(season => {
        const title = season.seasonNumber
          ? `${backend.translations.forKey(DetailTranslationKeys.detail_season)} ${season.seasonNumber}`
          : season.text.title ?? '';

        const sortedSeason: ISortedSeasons = { title, seasonId: season.seasonId, sortedPrograms: [] };

        const programs = _programs.filter(program => program.seasonId === season.seasonId);

        programs.forEach(program => {
          const schedules = _schedules.filter(schedule => schedule.programId === program.programId);

          schedules.forEach(schedule => {
            // check if there is still another schedule for the same channel also in replay (NOWO-285)
            if (timeStringToMs(schedule.published.end) < now) {
              const otherChannelInReplay = schedules.some(sched => {
                return (
                  sched.channelId === schedule.channelId &&
                  timeStringToMs(sched.published.end) < now &&
                  timeStringToMs(sched.published.end) > timeStringToMs(schedule.published.end)
                );
              });
              if (otherChannelInReplay) {
                return;
              }
            }

            const channel = channels?.find(ch => ch.channelId === schedule.channelId);
            // if no channel is found, discard schedule
            if (!channel) {
              return;
            }
            const tmp: ISortedProgram = {
              program,
              channel,
              schedule,
              season,
              allSchedules: schedules,
              series: series,
            };
            sortedSeason.sortedPrograms.push(tmp);
          });
        });

        sortedSeason.sortedPrograms.sort((a, b) => {
          if (a.program.episodeNumber && b.program.episodeNumber && a.program.episodeNumber < b.program.episodeNumber) {
            return -1;
          } else if (
            a.program.episodeNumber &&
            b.program.episodeNumber &&
            a.program.episodeNumber > b.program.episodeNumber
          ) {
            return 1;
          } else {
            if (parseInt(a.schedule.published.start, 10) < parseInt(b.schedule.published.start, 10)) {
              return -1;
            } else if (parseInt(a.schedule.published.start, 10) > parseInt(b.schedule.published.start, 10)) {
              return 1;
            } else {
              return 0;
            }
          }
        });

        if (sortedSeason && sortedSeason.sortedPrograms.length > 0) {
          sortedProgramsPerSeason.push(sortedSeason);
        }
      });
    }
    return sortedProgramsPerSeason;
  }

  static canWatchLive(schedule: IScheduleV5, channel: ChannelModel): boolean {
    if (!EpgUtil.scheduleIsLive(schedule) || !channel) {
      return false;
    }
    return channel.hasOneOfCustomerRights([StreamType.LINEAR, StreamType.DVBC]);
  }

  static canWatchReplay(schedule: IScheduleV5, channel: ChannelModel): boolean {
    if (!EpgUtil.scheduleIsReplay(schedule, channel) || !channel) {
      return false;
    }

    return channel.customerHasRight(StreamType.REPLAY);
  }

  static canPlayRecording(
    backend: Backend,
    recording: Recording,
    schedule: IRibbon2Schedule | IScheduleV5,
    channel: ChannelModel
  ) {
    if (recording) {
      // NPVR
      if (backend.sessionInfo?.npvr && recording.device === backend.sessionInfo.npvr.id) {
        if ((recording.isRecorded() || recording.isInProgress()) && channel.customerHasRight(StreamType.NPVR)) {
          if (!EpgUtil.scheduleHasBlackout(schedule.blackouts, BlackoutUtil.create(StreamType.NPVR))) {
            // no npvr blackout for ott
            return true;
          }
        }
      }
    }

    return false;
  }

  static getRecordingIcons(recording: Recording | undefined): { src: string }[] {
    if (!recording) return [];
    let recIcon;
    if (recording.isRecorded()) {
      if (recording.hasConflict() || recording.hasError()) {
        recIcon = RecordingIcons.error;
      } else {
        recIcon = RecordingIcons.recorded;
      }
    } else if (recording.isInProgress()) {
      if (recording.hasConflict()) {
        recIcon = RecordingIcons.inProgressConflict;
      } else if (recording.hasError()) {
        recIcon = RecordingIcons.inProgressError;
      } else {
        recIcon = RecordingIcons.inProgress;
      }
    } else {
      if (recording.hasConflict()) {
        recIcon = RecordingIcons.plannedConflict;
      } else {
        recIcon = RecordingIcons.planned;
      }
    }

    const result = [];
    if (recIcon) result.push(Utils.asset(recIcon));
    if (recording.deleteProtected) result.push(Utils.asset(RecordingIcons.locked));
    return result.map(src => {
      return {
        src,
      };
    });
  }

  static stringToBoolean(str: string): boolean | undefined {
    if (str === 'ON') return true;
    if (str === 'OFF') return false;
    if (str === 'true') return true;
    if (str === 'false') return false;
    return undefined;
  }
}
